import { defaultTo } from 'lodash';
import { JSX, useEffect } from 'react';
import { Heading, Layout } from '../../components/core';
import { PageContent } from '../../components/page';
import { useSearchParams } from '../../routes';
import { useTitle } from '../../utils/useTitle';
import { AuthErrorPage, parseSsoErrorNumberToType } from './components/AuthError';
import { useLoginRedirect } from './hooks/useLoginRedirect';
import { useSsoLogin } from './hooks/useSsoLogin';

const SsoLoadingPage = (): JSX.Element => {
  useTitle('Logging in');

  return (
    <PageContent testID="sso-response-loading-page">
      <Layout.Center>
        <Heading.h4>Logging you in...</Heading.h4>
      </Layout.Center>
    </PageContent>
  );
};

export const AuthenticationSsoResponsePage = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const loginRedirect = useLoginRedirect();

  const [login, { loading: loginLoading, error: loginError }] = useSsoLogin({
    onCompleted: () => {
      void loginRedirect();
    },
  });

  const redirectError = Number(defaultTo(searchParams.get('error'), 0));
  const tokenId = defaultTo(searchParams.get('tokenId'), '');
  const oneTimeToken = defaultTo(searchParams.get('token'), '');

  useEffect(() => {
    if (redirectError > 0) {
      // Don't attempt to log in if an error code was sent.
      return;
    }

    void login({ tokenId, oneTimeToken });
  }, []);

  const errorTitle = 'Login Error: Unable to Continue';

  if (redirectError > 0) {
    return (
      <AuthErrorPage errorType={parseSsoErrorNumberToType(redirectError)} title={errorTitle} />
    );
  }

  if (tokenId == null || oneTimeToken == null || tokenId === '' || oneTimeToken === '') {
    return <AuthErrorPage errorType="catchAll" title={errorTitle} />;
  }

  if (!loginLoading && loginError !== undefined) {
    // Something went wrong with the login-by-token process.
    return <AuthErrorPage errorType="catchAll" title={errorTitle} />;
  }

  // If loading is false, the user should be redirected shortly, so just continue showing loading page.
  return <SsoLoadingPage />;
};
